import {Editor, Element, Node, Text, Transforms, type NodeEntry} from "slate";

import {
    createListItemNode,
    createListItemTextNode,
    getNestedList,
    isListItemNode,
    isListItemTextNode,
    isListNode,
    mergeListWithPreviousSiblingList,
    moveListItemsToAnotherList,
    moveListToListItem,
} from "../helpers/list-helpers.ts";
import {getNextSibling, getPreviousSibling} from "../helpers/node-helpers.ts";

/**
 * This is a Slate plugin that adds our custom list-related behavior. The main
 * feature added here is a bunch of "normalization" to enforce a consistent
 * structure for our lists.
 *
 * List elements should always be structured like this:
 *  bulleted-list/numbered-list
 *    list-item
 *      list-item-text
 *        text
 *    list-item
 *      list-item-text
 *        text
 *    list-item
 *      list-item-text
 *      bulleted-list
 *        list-item
 *          list-item-text
 *            text
 *
 * Some important details:
 * - lists can be nested, with bulleted-list/numbered-list at the top
 * - list nodes will only contain list-item children
 * - list-item nodes must contain a single list-item-text, and optionally
 *   a second list node child (if there is a nested list under this item)
 * - list-item-text nodes can only contain inline text children - this is
 *   where the real "content" of a list item will end up
 *
 * The normalizations added here should enforce that structure/schema!
 */
export const withLists = (editor: Editor): Editor => {
    const {normalizeNode: originalNormalizeNode, isBlock} = editor;

    editor.isBlock = (element) =>
        element.type === "bulleted-list" ||
        element.type === "numbered-list" ||
        element.type === "lettered-list" ||
        isBlock(element);

    editor.normalizeNode = (entry, options) => {
        // Give each of our custom normalization passes a chance to run and
        // return true if no more normalization is needed for this node.
        if (
            normalizeListNode(editor, entry) ||
            normalizeListItemNode(editor, entry) ||
            normalizeListItemTextNode(editor, entry) ||
            normalizeOrphanListItemNode(editor, entry) ||
            normalizeOrphanListItemTextNode(editor, entry) ||
            normalizeOrphanNestedListNode(editor, entry) ||
            normalizeSiblingLists(editor, entry)
        ) {
            return;
        }

        // If none of them applied, fallback to the original normalize:
        originalNormalizeNode(entry, options);
    };

    return editor;
};

// adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeListChildren.ts
const normalizeListNode = (editor: Editor, entry: NodeEntry): boolean => {
    const [node, path] = entry;
    if (!isListNode(node)) {
        // This pass only applies to list nodes
        return false;
    }

    const children = Array.from(Node.children(editor, path));

    // All children of a list node have to be list-items, so we enforce that here:
    for (const [childNode, childPath] of children) {
        if (Text.isText(childNode)) {
            // This can happen during pasting

            // When pasting from MS Word there may be weird text nodes with some whitespace
            // characters. They're not expected to be deserialized so we remove them.
            if (childNode.text.trim() === "") {
                if (children.length > 1) {
                    Transforms.removeNodes(editor, {at: childPath});
                } else {
                    // If we're removing the only child, we may delete the whole list as well
                    // to avoid never-ending normalization (Slate will insert empty text node).
                    Transforms.removeNodes(editor, {at: path});
                }
                return true;
            }

            Transforms.wrapNodes(
                editor,
                createListItemNode({
                    children: [createListItemTextNode({children: [childNode]})],
                }),
                {at: childPath},
            );
            return true;
        }

        if (isListItemTextNode(childNode)) {
            Transforms.wrapNodes(editor, createListItemNode(), {
                at: childPath,
            });
            return true;
        }

        if (isListNode(childNode)) {
            // Wrap it into a list-item so that the list-item normalization
            // pass can take care of it.
            Transforms.wrapNodes(editor, createListItemNode(), {
                at: childPath,
            });
            return true;
        }

        if (!isListItemNode(childNode)) {
            Transforms.setNodes(editor, createListItemTextNode(), {
                at: childPath,
            });
            Transforms.wrapNodes(editor, createListItemNode(), {
                at: childPath,
            });
            return true;
        }
    }

    return false;
};

// adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeListItemChildren.ts
const normalizeListItemNode = (editor: Editor, entry: NodeEntry): boolean => {
    const [node, path] = entry;
    if (!isListItemNode(node)) {
        // This pass only applies to list-item nodes
        return false;
    }

    const children = Array.from(Node.children(editor, path));

    // A list-item can have a single list-item-text and optionally a single
    // extra list as a child. We enforce that here!
    for (const [childIndex, [childNode, childPath]] of children.entries()) {
        if (Text.isText(childNode) || editor.isInline(childNode)) {
            const listItemText = createListItemTextNode({
                children: [childNode],
            });

            Transforms.wrapNodes(editor, listItemText, {at: childPath});

            if (childIndex > 0) {
                const [previousChildNode] = children[childIndex - 1];

                if (isListItemTextNode(previousChildNode)) {
                    Transforms.mergeNodes(editor, {at: childPath});
                }
            }

            return true;
        }

        if (isListItemNode(childNode)) {
            Transforms.liftNodes(editor, {at: childPath});
            return true;
        }

        if (isListItemTextNode(childNode) && childIndex !== 0) {
            Transforms.wrapNodes(editor, createListItemNode(), {
                at: childPath,
            });
            return true;
        }

        if (!isListItemTextNode(childNode) && !isListNode(childNode)) {
            Transforms.setNodes(editor, createListItemTextNode(), {
                at: childPath,
            });
            return true;
        }
    }

    return false;
};

// adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeListItemTextChildren.ts
const normalizeListItemTextNode = (
    editor: Editor,
    entry: NodeEntry,
): boolean => {
    const [node, path] = entry;
    if (!isListItemTextNode(node)) {
        // This pass only applies to list-item-text nodes
        return false;
    }

    // Make sure that all children of list-item-texts are inline:
    for (const [childNode, childPath] of Node.children(editor, path)) {
        if (
            Element.isElement(childNode) &&
            !Editor.isInline(editor, childNode)
        ) {
            // And if not, unwrap them!
            Transforms.unwrapNodes(editor, {at: childPath});
            return true;
        }
    }

    return false;
};

/**
 * If "list-item" somehow (e.g. by deleting everything around it) ends up
 * at the root of the editor, we have to convert it into a paragraph.
 * adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeOrphanListItem.ts
 */
const normalizeOrphanListItemNode = (
    editor: Editor,
    entry: NodeEntry,
): boolean => {
    const [node, path] = entry;

    if (isElementOrEditor(node) && !isListNode(node)) {
        // We look for "list-item" nodes that are NOT under a "list" node
        for (const [index, child] of node.children.entries()) {
            if (Element.isElement(child) && isListItemNode(child)) {
                if (child.children.some(Text.isText)) {
                    Transforms.setNodes(
                        editor,
                        {type: "paragraph"},
                        {at: [...path, index]},
                    );
                } else {
                    Transforms.unwrapNodes(editor, {
                        at: [...path, index],
                        mode: "highest",
                    });
                }
                return true;
            }
        }
    }

    return false;
};

/**
 * If "list-item-text" somehow (e.g. by deleting everything around it) ends up
 * at the root of the editor, we have to convert it into a "default-block".
 * adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeOrphanListItemText.ts
 */
const normalizeOrphanListItemTextNode = (
    editor: Editor,
    entry: NodeEntry,
): boolean => {
    const [node, path] = entry;

    if (isElementOrEditor(node) && !isListItemNode(node)) {
        // We look for "list-item-text" nodes that are NOT under a "list-item" node
        for (const [index, child] of node.children.entries()) {
            if (Element.isElement(child) && isListItemTextNode(child)) {
                if (child.children.some(Text.isText)) {
                    Transforms.setNodes(
                        editor,
                        {type: "paragraph"},
                        {at: [...path, index]},
                    );
                } else {
                    Transforms.unwrapNodes(editor, {
                        at: [...path, index],
                        mode: "highest",
                    });
                }
                return true;
            }
        }
    }

    return false;
};

// adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizations/normalizeOrphanNestedList.ts
const normalizeOrphanNestedListNode = (
    editor: Editor,
    entry: NodeEntry,
): boolean => {
    const [node, path] = entry;
    if (!isListItemNode(node)) {
        // This function does not know how to normalize other nodes.
        return false;
    }

    const children = Array.from(Node.children(editor, path));

    if (children.length !== 1) {
        return false;
    }

    const [list] = children;
    const [listNode, listPath] = list;

    if (!isListNode(listNode)) {
        // If the first child is not a "list", then this fix does not apply.
        return false;
    }

    const previousListItem = getPreviousSibling(editor, path);

    if (previousListItem) {
        const [, previousListItemPath] = previousListItem;
        const previousListItemNestedList = getNestedList(
            editor,
            previousListItemPath,
        );

        if (previousListItemNestedList) {
            moveListItemsToAnotherList(editor, {
                at: list,
                to: previousListItemNestedList,
            });
        } else {
            moveListToListItem(editor, {
                at: list,
                to: previousListItem,
            });
        }
        // Remove now empty "list-item".
        Transforms.removeNodes(editor, {at: path});
    } else {
        // If there's no previous "list-item" to move nested "list" into, just lift it up.
        Transforms.unwrapNodes(editor, {at: listPath});
        Transforms.unwrapNodes(editor, {at: path});
    }

    return true;
};

/**
 * If there are 2 "lists" of the same type next to each other, merge them together.
 * If there are 2 nested "lists" next to each other, merge them together.
 * adapted from: https://github.com/prezly/slate/blob/7582983681019ae2341bb99daa871064e31ba4be/packages/slate-lists/src/normalizeNode.ts
 */
const normalizeSiblingLists = (editor: Editor, entry: NodeEntry): boolean => {
    const [node, path] = entry;
    const normalized = mergeListWithPreviousSiblingList(editor, [node, path]);
    if (normalized) {
        return true;
    }

    const nextSibling = getNextSibling(editor, path);
    if (nextSibling) {
        return mergeListWithPreviousSiblingList(editor, nextSibling);
    }

    return false;
};

const isElementOrEditor = (node: Node): node is Element | Editor => {
    return "children" in node;
};
